import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, Observable } from 'rxjs';
import { SUPER_ADMIN_PERMISSION } from '@shared/constants/auth';
import { logout, refresh } from '../store/auth.actions';
import { AUTH_FEATURE_KEY, AuthState } from '../store/auth.reducer';
import {
  isLoggedIn,
  selectAccessToken,
  selectRefreshToken,
  selectRememberMe,
  selectUserAuthorityNames,
  selectUserPermissionNames,
} from '../store/auth.selectors';
import { EnAuthOptionsOperator } from '@shared/enums/auth-options-operator.enum';
import { hasSuperAdminWildcard, matchAuthOptions } from '@shared/utils/auth-match.utils';
import { AbstractApiService } from '@shared/abstracts/abstract-api.service';
import { AuthSession, StoredAuthGrants } from '@shared/interfaces/auth-session.interface';
import { Storage } from '@shared/storage';
import {
  AuthorityDto,
  ClientDto,
  LoginResponseDto,
  PermissionDto,
  ProfileDto,
  RefreshTokenResponseDto,
  UpdateProfileDto,
} from '@shared/dto';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends AbstractApiService implements AuthSession {
  private readonly refreshTokenValue = toSignal(this.store.select(selectRefreshToken), { initialValue: null });

  /**
   * Store projections, not copies assigned in a subscribe. Gates that read
   * these from a computed re-run when permissions land after first paint
   * (CLAUDE.md §9).
   */
  readonly authorityNames = toSignal(this.store.select(selectUserAuthorityNames), { initialValue: [] as string[] });
  readonly permissionNames = toSignal(this.store.select(selectUserPermissionNames), { initialValue: [] as string[] });

  readonly accessToken$ = this.store.select(selectAccessToken);
  readonly rememberMe$ = this.store.select(selectRememberMe);
  readonly loggedIn$ = this.store.select(isLoggedIn);

  get endpoint(): string | null {
    return `${this.api$.value}/auth`;
  }

  constructor() {
    super();
    this.api$.next(this.env.get('api.base'));
  }

  refreshToken() {
    return this.http.post<RefreshTokenResponseDto>(`${this.endpoint}/token`, {grantType: 'refresh_token', refreshToken: this.refreshTokenValue() ?? undefined}).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  storeRefreshedTokens(access: string, refreshToken: string): void {
    this.store.dispatch(refresh({ access, refresh: refreshToken }));
  }

  readStoredGrants(): StoredAuthGrants | null {
    const auth = Storage.getJSON(AUTH_FEATURE_KEY) as AuthState | null;
    if (!auth) return null;
    return {
      authorityNames: (auth.authorities ?? []).map((authority) => authority.name),
      permissionNames: (auth.permissions ?? []).map((permission) => permission.name),
    };
  }

  login(email: string, password: string ) {
    return this.http.post<LoginResponseDto>(`${this.endpoint}/login`, {type: 'pwd', email, password}).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  logout(redirect: boolean, finalLocalUrl?: string, state?: Record<string, unknown>) {
    this.logger.debug(this.name, 'On logout');
    this.store.dispatch(logout());
    if (redirect) {
      this.router.navigateByUrl(finalLocalUrl ?? '/auth/login', { state })
        .catch((error) => this.logger.error(this.name, error));
    }
  }

  readProfile(): Observable<ProfileDto> {
    return this.http.get<ProfileDto>(`${this.endpoint}/profile`).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  updateProfile(dto: UpdateProfileDto): Observable<ProfileDto> {
    return this.http.put<ProfileDto>(`${this.endpoint}/profile`, dto).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  deleteAccount(): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/account`).pipe(
      catchError((error) => this.handleHttpErrorResponse(error)),
    );
  }

  readClient(): Observable<ClientDto> {
    return this.http.get<ClientDto>(`${this.endpoint}/client`).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  readAuthorities(): Observable<AuthorityDto[]> {
    return this.http.get<AuthorityDto[]>(`${this.endpoint}/authorities`).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  readPermissions(): Observable<PermissionDto[]> {
    return this.http.get<PermissionDto[]>(`${this.endpoint}/permissions`).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Always resolves on 200 whether or not the address is registered
   * (backend anti-enumeration). Do not branch UX on “email not found”.
   * Rate limited to one request every 45s (`RESET_PASSWORD_RESEND_SECONDS`) → 429.
   */
  forgotPassword(email: string) {
    return this.http.post(`${this.endpoint}/forgot-password`, { email }).pipe(
      catchError((error) => this.handleHttpErrorResponse(error)),
    );
  }

  hasAuthorities(searchParam: string | Array<string>, operator: EnAuthOptionsOperator = EnAuthOptionsOperator.AND): boolean {
    if (!searchParam) return true;
    if (typeof searchParam === "string") searchParam = [searchParam];
    return this._handleAuthOptionsOperator(searchParam, this.authorityNames(), operator);
  }

  hasPermissions(searchParam: string | Array<string>, operator: EnAuthOptionsOperator = EnAuthOptionsOperator.AND): boolean {
    if (!searchParam) return true;
    if (typeof searchParam === "string") searchParam = [searchParam];
    if (hasSuperAdminWildcard(this.permissionNames())) return true;
    return this._handleAuthOptionsOperator(searchParam, this.permissionNames(), operator);
  }

  private _handleAuthOptionsOperator(searchParam: string | Array<string>, list: readonly string[] = [], operator: EnAuthOptionsOperator = EnAuthOptionsOperator.AND): boolean {
    const params: string[] = typeof searchParam === "string" ? [searchParam] : searchParam;
    return matchAuthOptions(params, list, operator);
  }

  hasExactPermissions(searchParam: string | Array<string>, operator: EnAuthOptionsOperator = EnAuthOptionsOperator.AND): boolean {
    if (!searchParam) return true;
    if (typeof searchParam === "string") searchParam = [searchParam];
    // No wildcard bypass — checks literal assigned permissions only
    return this._handleAuthOptionsOperator(searchParam, this.permissionNames(), operator);
  }

  isSuperAdmin(): boolean {
    return this.hasPermissions(SUPER_ADMIN_PERMISSION);
  }
}
